import React from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "../../../../shared/components/ui/Button";
import { LoadingSpinner } from "../../../../shared/components/ui/LoadingSpinner";
import { FormField } from "../../../../shared/components/forms/FormField";
import { useReportGenerator } from "./useReportGenerator";
import type { BuildingOptionsResponse, ApiError } from "../../../../api/client/types/common.types";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import type { ReportType } from "../../utils/reportFilenames";
import {
  LABEL_DATE_RANGE,
  LABEL_BUILDING_OPTIONAL,
  OPTION_ALL_BUILDINGS,
  PLACEHOLDER_SELECT_DATES_FIRST,
  PLACEHOLDER_SEARCH_BUILDING,
  HINT_SELECT_DATES_FIRST,
  LOADING_BUILDINGS,
  LABEL_FORMAT,
  FORMAT_EXCEL,
  FORMAT_PDF,
  BUTTON_GENERATING,
  BUTTON_GENERATE_REPORT,
  NOTE_LABEL,
  NO_BUILDINGS_WITH_SWIMMERS_IN_RANGE,
} from "../../constants/reportMessages";

interface GetBuildingsRequest {
  start_date: string;
  end_date: string;
}

interface ReportGeneratorProps {
  title: string;
  description: string;
  filenamePrefix: string;
  reportType: ReportType;
  onGenerate: (start_date: string, end_date: string, building_id?: string, format?: "excel" | "pdf", building_name?: string, report_type_id?: number) => Promise<Blob | null>;
  buildingsFetcher?: UseMutationResult<BuildingOptionsResponse, ApiError, GetBuildingsRequest>;
  showBuildingFilter?: boolean;
  showFormatSelector?: boolean;
  maxRangeDays?: number;
  /** When true, shows a report type dropdown (only visible in Excel format). */
  showReportTypeFilter?: boolean;
  /** Message when no buildings have data in the selected date range. Defaults to swimmers message. */
  emptyBuildingsMessage?: string;
}

/**
 * ReportGenerator component for date range-based reports
 * 
 * This component handles:
 * - Date range selection (start and end date)
 * - Optional building filter (with dynamic building options fetching)
 * - Date validation (max 32 days range)
 * - Report generation and file download
 */

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  title,
  description,
  filenamePrefix,
  reportType,
  onGenerate,
  buildingsFetcher,
  showBuildingFilter = false,
  showFormatSelector = true,
  maxRangeDays = 32,
  showReportTypeFilter = false,
  emptyBuildingsMessage = NO_BUILDINGS_WITH_SWIMMERS_IN_RANGE,
}) => {
  const {
    startDate,
    endDate,
    buildingId,
    format,
    reportTypeId,
    reportTypes,
    isGenerating,
    errors,
    isValid,
    buildingOptions,
    isFetchingBuildings,
    handleDateRangeChange,
    handleBuildingIdChange,
    handleFormatChange,
    handleReportTypeChange,
    handleGenerateReport,
  } = useReportGenerator({
    reportType,
    onGenerate,
    buildingsFetcher,
    maxRangeDays,
  });

  // Check if both dates are selected to enable building filter
  const areDatesSelected = startDate !== "" && endDate !== "" && !errors.start_date && !errors.end_date;
  
  // Convert building options to SearchableSelect format
  const buildingSelectOptions: { value: string; label: string }[] = [
    { value: "", label: OPTION_ALL_BUILDINGS },
    ...buildingOptions.map((building) => ({
      value: building.id,
      label: building.name,
    })),
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>

      <div className="space-y-6">
        {/* Filters Section */}
        <div className="space-y-4">
          {/* Date Range Row - Compact Picker */}
          <DateRangePicker
            id={`date-range-${filenamePrefix}`}
            label={LABEL_DATE_RANGE}
            required
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            maxRangeDays={maxRangeDays}
            errorMessages={[errors.start_date, errors.end_date].filter(Boolean) as string[]}
          />

          {/* Format Selector */}
          {showFormatSelector && <div className="max-w-xs">
            <label
              htmlFor={`format-${filenamePrefix}`}
              className="block text-sm font-medium mb-2 text-gray-700"
            >
              {LABEL_FORMAT}
            </label>
            <select
              id={`format-${filenamePrefix}`}
              value={format}
              onChange={(e) => handleFormatChange(e.target.value as "excel" | "pdf")}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-lifeguards-blue focus:outline-none"
            >
              <option value="excel">{FORMAT_EXCEL}</option>
              <option value="pdf">{FORMAT_PDF}</option>
            </select>
          </div>}

          {/* Report Type Filter — Excel only */}
          {showReportTypeFilter && format === "excel" && (
            <div className="max-w-xs">
              <label
                htmlFor={`report-type-${filenamePrefix}`}
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Tipo de Novedad
              </label>
              <select
                id={`report-type-${filenamePrefix}`}
                value={reportTypeId ?? ""}
                onChange={(e) =>
                  handleReportTypeChange(e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-lifeguards-blue focus:outline-none"
              >
                <option value="">Todos los tipos</option>
                {reportTypes?.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Building Filter Row - Constrained Width (now last input) */}
          {showBuildingFilter && (
            <div className="max-w-md">
              <label
                htmlFor={`building-${filenamePrefix}`}
                className={`block text-sm font-medium mb-2 ${
                  !areDatesSelected ? "text-gray-400" : "text-gray-700"
                }`}
              >
                {LABEL_BUILDING_OPTIONAL}
              </label>
              <FormField
                type="searchableSelect"
                label=""
                searchableSelectProps={{
                  id: `building-${filenamePrefix}`,
                  options: buildingSelectOptions,
                  value: buildingId,
                  onChange: handleBuildingIdChange,
                  placeholder: !areDatesSelected
                    ? PLACEHOLDER_SELECT_DATES_FIRST
                    : PLACEHOLDER_SEARCH_BUILDING,
                  disabled: !areDatesSelected || isFetchingBuildings,
                }}
                error={errors.building_id}
                className="gap-0"
              />
              {!areDatesSelected && !errors.building_id && (
                <p className="mt-1 text-sm text-gray-500">{HINT_SELECT_DATES_FIRST}</p>
              )}
              {isFetchingBuildings && (
                <div className="mt-1 text-sm text-blue-600 flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  {LOADING_BUILDINGS}
                </div>
              )}
              {areDatesSelected && !isFetchingBuildings && buildingOptions.length === 0 && !errors.building_id && (
                <p className="mt-1 text-sm text-gray-500">{emptyBuildingsMessage}</p>
              )}
            </div>
          )}
        </div>

        {/* Button Row */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleGenerateReport}
            disabled={isGenerating || !isValid}
            className="w-full sm:w-auto min-w-[200px]"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                {BUTTON_GENERATING}
              </span>
            ) : (
              BUTTON_GENERATE_REPORT
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-md">
        <p className="text-sm text-gray-700">
          <strong>{NOTE_LABEL}:</strong> {description}
        </p>
      </div>
    </div>
  );
};

